import React from 'react';
import { fmtDate } from './lib/format';

/**
 * Date bounds for the paginated Detail RPCs, clamped to `detail_from` / `detail_to`
 * from `fn_get_search_console_dates` (spec v2 §10).
 *
 * This is NOT a picker for the page. The panel payload is a nightly snapshot computed
 * for a fixed 90-day window; its sentences, its ranks and its emphasis were all decided
 * on that window and are not recalculated. This control governs only the five paginated
 * RPCs, which take their own `p_start_date` / `p_end_date` and are the one place in the
 * page where the window is the reader's to choose.
 *
 * The bounds come from `detail_from` / `detail_to` and NOT from `window.start` /
 * `window.end` — §10 is explicit about that, and the backend addendum's §4 check
 * confirmed the stored range always extends past the page window on both sides. Using
 * the 90-day window here would hide history the tables can actually serve.
 *
 * Changing either bound resets the tables to page 1 (handled by `DetailTable`): the
 * embedded first page came from the 90-day window, so a range change followed by a
 * page-2 click would otherwise show two pages drawn from different windows.
 *
 * ── Comparison range (§10) ──────────────────────────────────────────────────────
 * "Two ranges side by side is two calls and a diff — nothing more is needed." Turning
 * comparison on reveals a second pair of bounds; the tables issue the second call and
 * render the difference. It is off by default because an owner has no use for it and §3
 * puts the cost of a dense default above the convenience of a specialist.
 *
 * ── Why the fields sit in a grid ────────────────────────────────────────────────
 * The two rows have labels of very different lengths — "From" against "Compare from" —
 * so laying them out as two independent flex rows put every field at a different x
 * position and gave each a different width, since a native date input sizes to its own
 * content plus the picker button. One grid shared by both rows fixes the label columns
 * to the widest label, and one width token on every input makes the four fields
 * identical. The result lines up whether or not the comparison row is showing.
 */

/** One width for every date field, so all four are identical at any label length. */
const FIELD_WIDTH = 'w-full sm:w-[11.5rem]';

const FIELD =
  'text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]';

const LEGEND =
  'text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap';

const DetailRangePicker = ({
  from,
  to,
  minDate,
  maxDate,
  onChange,
  disabled,
  compare = null,
  onCompareChange,
  // Compare only makes sense inside an expanded table (its prior-period columns render
  // there). The page passes false while every detail table is collapsed, and the toggle
  // is withheld rather than shown as an inert control.
  canCompare = true,
}) => {
  if (!minDate || !maxDate) return null;

  const comparing = canCompare && Boolean(compare?.from && compare?.to);

  // Turning comparison on has to seed a range, and the only defensible seed is the
  // equivalent window one year earlier — the same comparison the payload's own
  // `previous_period` uses. Seeding with the bounds of all stored history instead would
  // compare 90 days against 482 and put a meaningless delta on screen by default.
  const seedCompare = () => {
    const shiftYear = (d) => {
      if (!d) return null;
      const [y, m, day] = d.split('-');
      const shifted = `${Number(y) - 1}-${m}-${day}`;
      return shifted < minDate ? minDate : shifted;
    };
    onCompareChange({ from: shiftYear(from), to: shiftYear(to) });
  };

  return (
    <div className="flex flex-col gap-3">
      {/* One grid for both rows: columns 1 and 3 size to the widest label, so the fields
          in columns 2 and 4 line up across rows. Two columns on a phone — label above
          its own field would double the height of a control an owner never touches. */}
      <div className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_auto_auto_auto] gap-x-3 gap-y-2 items-center justify-items-start sm:justify-start">
        <label htmlFor="gsc-detail-from" className={LEGEND}>From</label>
        <input
          id="gsc-detail-from"
          type="date"
          className={`${FIELD} ${FIELD_WIDTH}`}
          value={from || ''}
          min={minDate}
          max={to || maxDate}
          disabled={disabled}
          onChange={(e) => onChange({ from: e.target.value || minDate, to })}
        />

        <label htmlFor="gsc-detail-to" className={LEGEND}>To</label>
        <input
          id="gsc-detail-to"
          type="date"
          className={`${FIELD} ${FIELD_WIDTH}`}
          value={to || ''}
          min={from || minDate}
          max={maxDate}
          disabled={disabled}
          onChange={(e) => onChange({ from, to: e.target.value || maxDate })}
        />

        {comparing && (
          <>
            <label htmlFor="gsc-compare-from" className={LEGEND}>Compare from</label>
            <input
              id="gsc-compare-from"
              type="date"
              className={`${FIELD} ${FIELD_WIDTH}`}
              value={compare.from || ''}
              min={minDate}
              max={compare.to || maxDate}
              disabled={disabled}
              onChange={(e) => onCompareChange({ from: e.target.value || minDate, to: compare.to })}
            />

            <label htmlFor="gsc-compare-to" className={LEGEND}>Compare to</label>
            <input
              id="gsc-compare-to"
              type="date"
              className={`${FIELD} ${FIELD_WIDTH}`}
              value={compare.to || ''}
              min={compare.from || minDate}
              max={maxDate}
              disabled={disabled}
              onChange={(e) => onCompareChange({ from: compare.from, to: e.target.value || maxDate })}
            />
          </>
        )}
      </div>

      {/* The caption and the toggle sit below the fields rather than trailing the last
          one: inline, they were what pushed the two rows out of alignment, and the
          caption is about the whole control rather than about the field beside it. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Available {fmtDate(minDate)} – {fmtDate(maxDate)}
        </p>
        {onCompareChange && canCompare && (
          <button
            type="button"
            data-testid="gsc-compare-toggle"
            aria-pressed={comparing}
            disabled={disabled}
            onClick={() => (comparing ? onCompareChange(null) : seedCompare())}
            className="text-sm font-medium text-[#32B1FF] hover:underline disabled:opacity-50 sm:ml-auto"
          >
            {comparing ? 'Stop comparing' : 'Compare with another period'}
          </button>
        )}
      </div>
    </div>
  );
};

export default DetailRangePicker;
